import { useState, useEffect } from 'react';
import { AlertTriangle, User, MapPin, Globe, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Validation patterns for detecting invalid data
const INVALID_NAME_PATTERNS = [
  /^[a-z]{1,2}$/i,           // Single letter or two letters
  /^test/i,                   // Starts with "test"
  /^asdf/i,                   // Keyboard mash
  /^qwer/i,                   // Keyboard mash
  /^xxx/i,                    // xxx
  /^aaa/i,                    // Repeated letters
  /^bbb/i,
  /^123/,                     // Numbers
  /^fake/i,                   // Fake
  /^name/i,                   // "name"
  /^first/i,                  // "first" or "firstname"
  /^last/i,                   // "last" or "lastname"
  /^user/i,                   // "user"
  /^customer/i,               // "customer"
  /^null$/i,                  // "null"
  /^undefined$/i,             // "undefined"
  /^n\/a$/i,                  // "n/a"
  /^none$/i,                  // "none"
  /^\./,                      // Starts with period
  /^-/,                       // Starts with dash
];

const INVALID_ADDRESS_PATTERNS = [
  /^[0-9]{1,3}$/,             // Just numbers like "123"
  /^test/i,                   // Starts with "test"
  /^asdf/i,                   // Keyboard mash
  /^qwer/i,                   // Keyboard mash
  /^xxx/i,                    // xxx
  /^aaa/i,                    // Repeated letters
  /^fake/i,                   // Fake
  /^address/i,                // "address"
  /^street/i,                 // Just "street"
  /^null$/i,                  // "null"
  /^undefined$/i,             // "undefined"
  /^n\/a$/i,                  // "n/a"
  /^none$/i,                  // "none"
  /^tbd$/i,                   // "tbd"
  /^na$/i,                    // "na"
  /^\./,                      // Starts with period
  /^-$/,                      // Just a dash
];

// Comprehensive list of supported countries (PayPal G&S + Forwarding)
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  // Asia Pacific
  { code: 'KR', name: 'South Korea' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'SG', name: 'Singapore' },
  { code: 'PH', name: 'Philippines' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'IN', name: 'India' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'NP', name: 'Nepal' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'MN', name: 'Mongolia' },
  // Oceania
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'FJ', name: 'Fiji' },
  // Europe
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'IE', name: 'Ireland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IS', name: 'Iceland' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'RS', name: 'Serbia' },
  { code: 'GR', name: 'Greece' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LV', name: 'Latvia' },
  { code: 'EE', name: 'Estonia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'TR', name: 'Turkey' },
  { code: 'GE', name: 'Georgia' },
  // Middle East
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'IL', name: 'Israel' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'OM', name: 'Oman' },
  { code: 'JO', name: 'Jordan' },
  { code: 'EG', name: 'Egypt' },
  // Americas
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'PA', name: 'Panama' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'PR', name: 'Puerto Rico' },
  // Africa
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'MA', name: 'Morocco' },
  { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' },
];

// US States
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington DC' }
];

// Validation functions
export const validateName = (name) => {
  if (!name || typeof name !== 'string') return { valid: false, reason: 'Name is required' };
  
  const trimmed = name.trim();
  
  // Must have at least 2 characters
  if (trimmed.length < 2) {
    return { valid: false, reason: 'Name is too short' };
  }
  
  // Must have first AND last name (at least 2 words)
  const words = trimmed.split(/\s+/).filter(w => w.length > 0);
  if (words.length < 2) {
    return { valid: false, reason: 'Please enter your full name (first and last)' };
  }
  
  // Check each word is at least 2 characters (allows for middle initials like "J.")
  const hasValidFirstAndLast = words[0].length >= 2 && words[words.length - 1].length >= 2;
  if (!hasValidFirstAndLast) {
    return { valid: false, reason: 'First and last name must be at least 2 characters each' };
  }
  
  // Check against invalid patterns
  for (const pattern of INVALID_NAME_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { valid: false, reason: 'Please enter a valid name' };
    }
  }
  
  return { valid: true };
};

export const validateAddress = (address) => {
  if (!address || typeof address !== 'string') return { valid: false, reason: 'Address is required' };
  
  const trimmed = address.trim();
  
  // Must have at least 5 characters
  if (trimmed.length < 5) {
    return { valid: false, reason: 'Address is too short' };
  }
  
  // Check against invalid patterns
  for (const pattern of INVALID_ADDRESS_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { valid: false, reason: 'Please enter a valid street address' };
    }
  }
  
  // Should contain at least one number (street number)
  if (!/\d/.test(trimmed)) {
    return { valid: false, reason: 'Address should include a street number' };
  }
  
  return { valid: true };
};

export const validateCountry = (country) => {
  if (!country || typeof country !== 'string') return { valid: false, reason: 'Country is required' };
  
  const trimmed = country.trim();
  
  // Check if it's a valid country code
  const validCountry = COUNTRIES.find(c => c.code === trimmed);
  if (!validCountry) {
    // Check for legacy "OTHER" value
    if (trimmed === 'OTHER') {
      return { valid: false, reason: 'Please select a specific country from the list' };
    }
    return { valid: false, reason: 'Please select a valid country' };
  }
  
  return { valid: true };
};

// Check all profile validation issues
export const getProfileValidationIssues = (profile) => {
  const issues = [];
  
  if (!profile) {
    return [{ field: 'profile', reason: 'Profile not loaded' }];
  }
  
  const nameValidation = validateName(profile.full_name);
  if (!nameValidation.valid) {
    issues.push({ field: 'full_name', reason: nameValidation.reason });
  }
  
  const addressValidation = validateAddress(profile.address);
  if (!addressValidation.valid) {
    issues.push({ field: 'address', reason: addressValidation.reason });
  }
  
  const countryValidation = validateCountry(profile.country);
  if (!countryValidation.valid) {
    issues.push({ field: 'country', reason: countryValidation.reason });
  }
  
  // Check city
  if (!profile.city || profile.city.trim().length < 2) {
    issues.push({ field: 'city', reason: 'City is required' });
  }
  
  // Check state (for US addresses)
  if (profile.country === 'US' && (!profile.state || profile.state.trim().length < 2)) {
    issues.push({ field: 'state', reason: 'State is required' });
  }
  
  // Check zip code
  if (!profile.zip_code || profile.zip_code.trim().length < 3) {
    issues.push({ field: 'zip_code', reason: 'ZIP/Postal code is required' });
  }
  
  // International users must have apt/unit (forwarding service ID)
  if (profile.country && profile.country !== 'US' && (!profile.apt_unit || profile.apt_unit.trim().length < 1)) {
    issues.push({ field: 'apt_unit', reason: 'International customers must enter forwarding service suite/unit number' });
  }
  
  return issues;
};

// The modal component
export default function ProfileValidationModal({ userProfile, getAuthHeader, onValidated, flagReason }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    country: '',
    address: '',
    apt_unit: '',
    city: '',
    state: '',
    zip_code: ''
  });
  const [issues, setIssues] = useState([]);
  
  // Check if this flag requires admin approval (user can't self-unflag)
  const requiresAdminApproval = userProfile?.require_admin_approval;

  useEffect(() => {
    if (userProfile) {
      setForm({
        full_name: userProfile.full_name || '',
        country: userProfile.country || '',
        address: userProfile.address || '',
        apt_unit: userProfile.apt_unit || '',
        city: userProfile.city || '',
        state: userProfile.state?.toUpperCase() || '',  // Ensure state is uppercase to match Select options
        zip_code: userProfile.zip_code || ''
      });
      setIssues(getProfileValidationIssues(userProfile));
    }
  }, [userProfile]);

  const hasIssue = (field) => issues.some(i => i.field === field);
  const getIssueReason = (field) => issues.find(i => i.field === field)?.reason || '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value) => {
    setForm(prev => ({ ...prev, country: value }));
  };

  const handleStateChange = (value) => {
    setForm(prev => ({ ...prev, state: value }));
  };

  const handleSave = async () => {
    // If requires admin approval, show error and don't try to unflag
    if (requiresAdminApproval) {
      toast.error('Your account requires admin approval. Please contact support.');
      return;
    }
    
    // Validate the form
    const newIssues = getProfileValidationIssues(form);
    setIssues(newIssues);
    
    if (newIssues.length > 0) {
      toast.error('Please fix all highlighted fields');
      return;
    }
    
    setSaving(true);
    try {
      // Save the profile
      await axios.put(`${API}/user/profile`, form, {
        headers: getAuthHeader()
      });
      
      // Auto-unflag the user after successful profile update
      try {
        const unflagResponse = await axios.post(`${API}/user/request-unflag`, {}, {
          headers: getAuthHeader()
        });
        toast.success('Profile updated successfully');
        onValidated();
      } catch (unflagError) {
        // Check if it's because admin approval is required
        if (unflagError.response?.status === 403) {
          toast.error('Your account requires admin approval. Your changes have been saved but an admin must verify your account.');
        } else {
          // Profile was saved, just couldn't unflag
          toast.success('Profile saved. Awaiting admin review.');
          onValidated();
        }
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isInternational = form.country && form.country !== 'US';

  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-red-500/50 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`${requiresAdminApproval ? 'bg-orange-500/20 border-orange-500/50' : 'bg-red-500/20 border-red-500/50'} border-b p-4`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-6 h-6 ${requiresAdminApproval ? 'text-orange-500' : 'text-red-500'}`} />
            <div>
              <h2 className={`font-mono text-lg ${requiresAdminApproval ? 'text-orange-500' : 'text-red-500'} font-bold`}>
                {requiresAdminApproval ? 'ACCOUNT PENDING ADMIN REVIEW' : 'ACCOUNT VERIFICATION REQUIRED'}
              </h2>
              <p className="font-mono text-xs text-zinc-400 mt-1">
                {requiresAdminApproval 
                  ? 'Your account has been flagged for admin review. You cannot make changes until an admin approves your account.'
                  : (flagReason || 'Please correct the following information to continue')}
              </p>
            </div>
          </div>
        </div>

        {/* Admin Approval Required Message */}
        {requiresAdminApproval && (
          <div className="p-4 bg-orange-500/10 border-b border-orange-500/30">
            <p className="font-mono text-sm text-orange-400">
              YOUR ACCOUNT REQUIRES ADMIN APPROVAL
            </p>
            <p className="font-mono text-xs text-zinc-400 mt-2">
              An administrator must review and approve your account before you can access the site. 
              Please contact support if you believe this is an error.
            </p>
            <p className="font-mono text-xs text-zinc-500 mt-2">
              Reason: {flagReason || 'Profile verification required'}
            </p>
          </div>
        )}

        {/* Issues Summary - only show if not admin approval required */}
        {!requiresAdminApproval && (
          <div className="p-4 border-b border-zinc-800">
            <p className="font-mono text-xs text-zinc-500 mb-2">PLEASE VERIFY YOUR INFORMATION:</p>
            <ul className="space-y-1">
              {issues.map((issue, idx) => (
                <li key={idx} className="font-mono text-xs text-red-400 flex items-center gap-2">
                  <X className="w-3 h-3" />
                  {issue.reason}
                </li>
              ))}
              {issues.length === 0 && (
                <li className="font-mono text-xs text-zinc-400">
                  Please verify all fields are accurate and save to continue
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Form - disabled if requires admin approval */}
        <div className={`p-4 space-y-4 ${requiresAdminApproval ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Full Name */}
          <div>
            <label className="block font-mono text-xs text-zinc-500 mb-1">
              FULL NAME (FIRST & LAST) *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                placeholder="John Smith"
                disabled={requiresAdminApproval}
                className={`pl-10 h-10 bg-zinc-800 text-white font-mono text-sm rounded-none ${
                  hasIssue('full_name') ? 'border-red-500 focus:border-red-500' : 'border-zinc-700'
                }`}
                data-testid="validation-full-name"
              />
            </div>
            {hasIssue('full_name') && (
              <p className="font-mono text-[10px] text-red-400 mt-1">{getIssueReason('full_name')}</p>
            )}
          </div>

          {/* Country */}
          <div>
            <label className="block font-mono text-xs text-zinc-500 mb-1">
              COUNTRY *
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 z-10" />
              <Select value={form.country} onValueChange={handleCountryChange}>
                <SelectTrigger 
                  className={`pl-10 h-10 bg-zinc-800 text-white font-mono text-sm rounded-none ${
                    hasIssue('country') ? 'border-red-500' : 'border-zinc-700'
                  }`}
                  data-testid="validation-country"
                >
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {COUNTRIES.map(country => (
                    <SelectItem 
                      key={country.code} 
                      value={country.code}
                      className="font-mono text-sm text-white hover:bg-zinc-700"
                    >
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasIssue('country') && (
              <p className="font-mono text-[10px] text-red-400 mt-1">{getIssueReason('country')}</p>
            )}
          </div>

          {/* International Notice */}
          {isInternational && (
            <div className="bg-tiffany-500/10 border border-tiffany-500/30 p-3">
              <p className="font-mono text-xs text-tiffany-500">
                INTERNATIONAL CUSTOMER - Enter your US forwarding service address below
              </p>
            </div>
          )}

          {/* Address */}
          <div>
            <label className="block font-mono text-xs text-zinc-500 mb-1">
              STREET ADDRESS *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder={isInternational ? "Your US forwarding address" : "123 Main Street"}
                className={`pl-10 h-10 bg-zinc-800 text-white font-mono text-sm rounded-none ${
                  hasIssue('address') ? 'border-red-500 focus:border-red-500' : 'border-zinc-700'
                }`}
                data-testid="validation-address"
              />
            </div>
            {hasIssue('address') && (
              <p className="font-mono text-[10px] text-red-400 mt-1">{getIssueReason('address')}</p>
            )}
          </div>

          {/* Apt/Unit */}
          <div>
            <label className="block font-mono text-xs text-zinc-500 mb-1">
              APT / UNIT / SUITE {isInternational && <span className="text-red-400">*</span>}
            </label>
            <Input
              name="apt_unit"
              value={form.apt_unit}
              onChange={handleChange}
              placeholder={isInternational ? "Your forwarding service suite/unit #" : "Apt 4B (optional)"}
              className={`h-10 bg-zinc-800 text-white font-mono text-sm rounded-none ${
                hasIssue('apt_unit') ? 'border-red-500 focus:border-red-500' : 'border-zinc-700'
              }`}
              data-testid="validation-apt-unit"
            />
            {hasIssue('apt_unit') && (
              <p className="font-mono text-[10px] text-red-400 mt-1">{getIssueReason('apt_unit')}</p>
            )}
          </div>

          {/* City, State, Zip Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-mono text-xs text-zinc-500 mb-1">CITY *</label>
              <Input
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="City"
                className={`h-10 bg-zinc-800 text-white font-mono text-sm rounded-none ${
                  hasIssue('city') ? 'border-red-500 focus:border-red-500' : 'border-zinc-700'
                }`}
                data-testid="validation-city"
              />
              {hasIssue('city') && (
                <p className="font-mono text-[10px] text-red-400 mt-1">{getIssueReason('city')}</p>
              )}
            </div>
            
            <div>
              <label className="block font-mono text-xs text-zinc-500 mb-1">
                STATE *
              </label>
              <Select value={form.state} onValueChange={handleStateChange}>
                <SelectTrigger 
                  className={`h-10 bg-zinc-800 text-white font-mono text-sm rounded-none ${
                    hasIssue('state') ? 'border-red-500' : 'border-zinc-700'
                  }`}
                  data-testid="validation-state"
                >
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {US_STATES.map(state => (
                    <SelectItem 
                      key={state.code} 
                      value={state.code}
                      className="font-mono text-sm text-white hover:bg-zinc-700"
                    >
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasIssue('state') && (
                <p className="font-mono text-[10px] text-red-400 mt-1">{getIssueReason('state')}</p>
              )}
            </div>
          </div>

          {/* Zip Code */}
          <div>
            <label className="block font-mono text-xs text-zinc-500 mb-1">ZIP / POSTAL CODE *</label>
            <Input
              name="zip_code"
              value={form.zip_code}
              onChange={handleChange}
              placeholder="12345"
              className={`h-10 bg-zinc-800 text-white font-mono text-sm rounded-none ${
                hasIssue('zip_code') ? 'border-red-500 focus:border-red-500' : 'border-zinc-700'
              }`}
              data-testid="validation-zip"
            />
            {hasIssue('zip_code') && (
              <p className="font-mono text-[10px] text-red-400 mt-1">{getIssueReason('zip_code')}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800">
          {requiresAdminApproval ? (
            <div className="text-center">
              <p className="font-mono text-sm text-orange-400 mb-2">
                AWAITING ADMIN APPROVAL
              </p>
              <p className="font-mono text-[10px] text-zinc-500">
                Please contact support if you need assistance
              </p>
            </div>
          ) : (
            <>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full h-12 bg-tiffany-500 hover:bg-tiffany-400 text-black font-mono font-bold rounded-none"
                data-testid="validation-save-btn"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    SAVING...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    SAVE & CONTINUE
                  </span>
                )}
              </Button>
              <p className="font-mono text-[10px] text-zinc-500 text-center mt-3">
                You must correct all issues before you can continue browsing
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
